import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, FlaskConical, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const INK_TYPES = ["Solvent-based", "Water-based", "UV-curable", "EB-curable", "Energy-curable", "Oil-based"];
const COLOR_FAMILIES = ["Process", "Spot", "White", "Metallic", "Fluorescent", "Extender"];

const EMPTY = { name: "", product_code: "", ink_type: "", substrate_family: "", color_family: "",
                voc_percent: "", fda_compliant: false, viscosity_seconds: "", density: "",
                sds_url: "", tds_url: "", notes: "" };

export default function InkProducts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/ink/products").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setLoading(true);
    try {
      await api.post("/ink/products", {
        ...form,
        voc_percent: form.voc_percent ? parseFloat(form.voc_percent) : null,
        viscosity_seconds: form.viscosity_seconds ? parseFloat(form.viscosity_seconds) : null,
        density: form.density ? parseFloat(form.density) : null,
      });
      toast.success("Product added");
      setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error("Save failed"); }
    finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/ink/products/${id}`);
    load();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">
            <Link to="/app/ink" className="hover:text-primary">Ink &amp; Print</Link> / Products
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-2" data-testid="ink-products-title">
            Product Knowledge Center
          </h1>
          <p className="text-sm text-muted-foreground mt-2">Ink specs · resins · pigments · additives · SDS/TDS · VOC · FDA</p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="add-product-btn">
          <Plus className="w-4 h-4 mr-2" /> Add product
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-16 text-center bg-card/40">
          <FlaskConical className="w-8 h-8 mx-auto text-muted-foreground" />
          <div className="font-display text-lg mt-4">No products yet</div>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Add your first ink, resin, pigment, or additive. Everything you enter here is available to the Copilot when generating customer reports.
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <div key={p.id} className="border border-border bg-card p-5 rounded-sm" data-testid={`product-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display font-bold text-lg leading-tight">{p.name}</div>
                  {p.product_code && <div className="text-xs font-mono-plex text-muted-foreground mt-0.5">{p.product_code}</div>}
                </div>
                <button onClick={() => remove(p.id)} className="text-muted-foreground hover:text-destructive" data-testid={`del-product-${p.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {p.ink_type && <Badge className="rounded-sm font-mono-plex text-[10px] uppercase tracking-widest bg-primary/10 text-primary hover:bg-primary/10">{p.ink_type}</Badge>}
                {p.color_family && <Badge className="rounded-sm font-mono-plex text-[10px] uppercase tracking-widest bg-secondary text-foreground">{p.color_family}</Badge>}
                {p.fda_compliant && <Badge className="rounded-sm font-mono-plex text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-800 hover:bg-emerald-100">FDA</Badge>}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                {p.voc_percent != null && <div><span className="opacity-70">VOC:</span> {p.voc_percent}%</div>}
                {p.viscosity_seconds != null && <div><span className="opacity-70">Visc:</span> {p.viscosity_seconds}s</div>}
                {p.density != null && <div><span className="opacity-70">Density:</span> {p.density}</div>}
                {p.substrate_family && <div className="col-span-2"><span className="opacity-70">Substrate:</span> {p.substrate_family}</div>}
              </div>
              {(p.sds_url || p.tds_url) && (
                <div className="mt-3 pt-3 border-t border-border flex gap-3 text-xs">
                  {p.sds_url && <a href={p.sds_url} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> SDS</a>}
                  {p.tds_url && <a href={p.tds_url} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> TDS</a>}
                </div>
              )}
              {p.notes && <p className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground leading-relaxed">{p.notes}</p>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="overline text-accent">◉ NEW PRODUCT</div>
            <DialogTitle className="font-display text-2xl font-bold tracking-tight">Add ink / resin / pigment / additive</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Name *</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="product-name-input" required />
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Product code</label>
                <Input value={form.product_code} onChange={(e) => setForm({ ...form, product_code: e.target.value })} className="rounded-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Ink type</label>
                <Select value={form.ink_type || "none"} onValueChange={(v) => setForm({ ...form, ink_type: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Choose" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {INK_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Color family</label>
                <Select value={form.color_family || "none"} onValueChange={(v) => setForm({ ...form, color_family: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Choose" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {COLOR_FAMILIES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-1.5">Substrate family</label>
              <Input value={form.substrate_family} onChange={(e) => setForm({ ...form, substrate_family: e.target.value })} className="rounded-sm" placeholder="e.g. BOPP film, corrugated E-flute" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="overline text-muted-foreground block mb-1.5">VOC %</label>
                <Input type="number" step="0.1" value={form.voc_percent} onChange={(e) => setForm({ ...form, voc_percent: e.target.value })} className="rounded-sm" />
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Visc (sec)</label>
                <Input type="number" step="0.1" value={form.viscosity_seconds} onChange={(e) => setForm({ ...form, viscosity_seconds: e.target.value })} className="rounded-sm" />
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Density</label>
                <Input type="number" step="0.01" value={form.density} onChange={(e) => setForm({ ...form, density: e.target.value })} className="rounded-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline text-muted-foreground block mb-1.5">SDS URL</label>
                <Input value={form.sds_url} onChange={(e) => setForm({ ...form, sds_url: e.target.value })} className="rounded-sm" placeholder="https://" />
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-1.5">TDS URL</label>
                <Input value={form.tds_url} onChange={(e) => setForm({ ...form, tds_url: e.target.value })} className="rounded-sm" placeholder="https://" />
              </div>
            </div>
            <label className="flex items-center gap-2 pt-1">
              <input type="checkbox" checked={form.fda_compliant} onChange={(e) => setForm({ ...form, fda_compliant: e.target.checked })} data-testid="fda-checkbox" />
              <span className="text-sm">FDA food-contact compliant</span>
            </label>
            <div>
              <label className="overline text-muted-foreground block mb-1.5">Notes</label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm resize-none" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="save-product-btn">
              {loading ? "Saving…" : "Save product"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
